import styled from '@emotion/styled';
import React, { FC } from 'react';
import { StorageData, useStorage } from '~Components/Storage';
import Toggle from '~ComponentsImproved/Toggle';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useButton, useI18n, useLogger, useSelector } from '~Hooks';
import { SettingsBlock } from '~Scenes/Settings-v2';
import Adyen from '~Scenes/Settings-v2/Scenes/Adyen';
import { BlueButton, Box, Button } from '~Styles';

const AdyenWrapper = styled.div`
  margin-top: 48px;

  & > div {
    padding: 0;
  }
`;

const PaymentsSettings: FC = () => {
  const { i18n } = useI18n();
  const logger = useLogger('settings-v2');
  const [storage, commitToStorage, storageLoading] = useStorage();
  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const hasCash = paymentMethods.find((method) => method.id === 'CASH')?.active;
  const hasZettle = paymentMethods.find((method) => method.id === 'IZETTLE')?.active;
  const hasAdyen = paymentMethods.find((method) => method.id === 'ADYEN')?.active;
  const hasInvoice = paymentMethods.find((method) => method.id === 'INVOICE')?.active;

  function updateStorage(data: Partial<StorageData>) {
    logger.info('Updating Settings (Payments Settings)', { fields: data });
    commitToStorage(data);
  }

  const zettleButton = useButton({
    onClick: () => {
      logger.info('Opening iZettle settings');
      window.postMessageBridge({ showIZettleSettings: true });
    },
  });

  if (storageLoading) {
    return null;
  }

  return (
    <Box padding="large">
      <Heading level={4} bottom={8} color="var(--colorWhite)">
        {i18n('Settings.Title')}
      </Heading>
      <SettingsBlock>
        <Toggle
          label={i18n('Settings.EnableTipping.Text')}
          settingKey="enableTipping"
          initialValue={storage.enableTipping}
          onToggle={() => {
            logger.info('Updating Settings (Payments Settings)', {
              fields: { enableTipping: !storage.enableTipping },
            });
            updateStorage({ enableTipping: !storage.enableTipping });
          }}
          fullWidth
          darkBackground
          lightText
        />
        {hasCash && (
          <Toggle
            label={i18n('Disable.Cash.Text')}
            settingKey="disableCash"
            initialValue={storage.disableCash}
            onToggle={() => {
              logger.info('Updating Settings (Payments Settings)', {
                fields: { disableCash: !storage.disableCash },
              });
              updateStorage({ disableCash: !storage.disableCash });
            }}
            fullWidth
            darkBackground
            lightText
          />
        )}
        {hasInvoice && (
          <Toggle
            label={i18n('Settings.EnableInvoiceCustomerCreation.Text')}
            settingKey="enableInvoiceCustomerCreation"
            initialValue={storage.enableInvoiceCustomerCreation}
            onToggle={() => {
              logger.info('Updating Settings (Payments Settings)', {
                fields: { enableInvoiceCustomerCreation: !storage.enableInvoiceCustomerCreation },
              });
              updateStorage({ enableInvoiceCustomerCreation: !storage.enableInvoiceCustomerCreation });
            }}
            fullWidth
            darkBackground
            lightText
          />
        )}
      </SettingsBlock>
      {hasZettle && (
        <Box marginTop="large">
          <BlueButton {...zettleButton}>{i18n('Settings.Payments.OpenZettle')}</BlueButton>
        </Box>
      )}
      {hasAdyen && (
        <AdyenWrapper>
          <Adyen />
        </AdyenWrapper>
      )}
    </Box>
  );
};

export default PaymentsSettings;
